import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CurrencyInr, ArrowRight, ChartLineUp } from "@phosphor-icons/react";

const PLAN_TONE = {
    free: "bg-[#F5F5F5] text-[#525252]",
    pro: "bg-cobalt/10 text-cobalt",
    enterprise: "bg-ink text-splash",
};

const fmtINR = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;

export default function SuperAdminSubscriptions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    useEffect(() => {
        api.get("/superadmin/subscriptions")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load subscriptions"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="max-w-6xl mx-auto p-6 text-sm text-[#A3A3A3]">Loading…</div>;
    if (!data) return null;
    const rows = filter === "all" ? data.items : data.items.filter((i) => i.plan === filter);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="superadmin-subscriptions">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1">Platform revenue</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Subscriptions</h1>
            <p className="text-sm text-[#525252] mb-6">Cross-tenant subscription state · MRR aggregate · plan mix.</p>

            {/* Aggregate */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-white border border-[#E5E5E5] p-5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                        <CurrencyInr size={12} weight="bold" /> MRR
                    </p>
                    <p className="font-heading text-3xl font-black tracking-tighter">{fmtINR(data.aggregate.mrr_inr)}</p>
                </div>
                <div className="bg-white border border-[#E5E5E5] p-5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2 flex items-center gap-1.5">
                        <ChartLineUp size={12} weight="bold" /> ARR (proj.)
                    </p>
                    <p className="font-heading text-3xl font-black tracking-tighter">{fmtINR(data.aggregate.arr_inr)}</p>
                </div>
                {["pro", "enterprise"].map((p) => (
                    <div key={p} className="bg-white border border-[#E5E5E5] p-5">
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">{p} tenants</p>
                        <p className="font-heading text-3xl font-black tracking-tighter">{data.aggregate.by_plan[p]}</p>
                    </div>
                ))}
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-2 mb-4">
                {["all", "free", "pro", "enterprise"].map((p) => (
                    <button key={p} onClick={() => setFilter(p)}
                            data-testid={`subs-filter-${p}`}
                            className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                                filter === p ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                            }`}>
                        {p}
                    </button>
                ))}
            </div>

            {/* Mobile cards (sm-) */}
            <div className="sm:hidden space-y-3">
                {rows.map((w) => (
                    <Link key={w.id} to={`/superadmin/workspaces/${w.id}`}
                          data-testid={`subs-card-${w.slug}`}
                          className="block bg-white border border-[#E5E5E5] p-4 hover:border-ink transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <p className="font-heading font-bold tracking-tight truncate">{w.name}</p>
                                <p className="text-[10px] font-mono text-[#A3A3A3] truncate">{w.slug}</p>
                            </div>
                            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 shrink-0 ${PLAN_TONE[w.plan] || PLAN_TONE.free}`}>{w.plan}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#F5F5F5] text-xs">
                            <div>
                                <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Seats</p>
                                <p className="font-heading font-black">{w.seats_used || 0}</p>
                            </div>
                            <div>
                                <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">MRR</p>
                                <p className="font-heading font-black">{fmtINR(w.mrr_inr)}</p>
                            </div>
                            <div>
                                <p className="text-[9px] uppercase tracking-wider text-[#A3A3A3]">Overrides</p>
                                <p className="font-heading font-black">{Object.keys(w.feature_overrides || {}).length || "—"}</p>
                            </div>
                        </div>
                    </Link>
                ))}
                {rows.length === 0 && (
                    <div className="bg-white border border-[#E5E5E5] p-8 text-center text-sm text-[#A3A3A3]">
                        No workspaces match this filter.
                    </div>
                )}
            </div>

            {/* Desktop table (sm+) */}
            <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                        <tr className="text-left">
                            {["Workspace", "Slug", "Plan", "Seats", "MRR", "Overrides", ""].map((h) => (
                                <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((w) => (
                            <tr key={w.id} data-testid={`subs-row-${w.slug}`} className="border-b border-[#F5F5F5]">
                                <td className="px-4 py-3 font-heading font-bold">{w.name}</td>
                                <td className="px-4 py-3 text-xs font-mono text-[#525252]">{w.slug}</td>
                                <td className="px-4 py-3">
                                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${PLAN_TONE[w.plan] || PLAN_TONE.free}`}>{w.plan}</span>
                                </td>
                                <td className="px-4 py-3 font-heading font-black">{w.seats_used || 0}</td>
                                <td className="px-4 py-3 font-heading font-black">{fmtINR(w.mrr_inr)}</td>
                                <td className="px-4 py-3 text-xs text-[#525252]">
                                    {Object.keys(w.feature_overrides || {}).length || "—"}
                                </td>
                                <td className="px-4 py-3">
                                    <Link to={`/superadmin/workspaces/${w.id}`}
                                          data-testid={`subs-manage-${w.slug}`}
                                          className="text-cobalt hover:text-ink inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider">
                                        Manage <ArrowRight size={12} weight="bold" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-sm text-[#A3A3A3]">
                                No workspaces match this filter.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
